import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChangeTitle,
  decrypt,
  encryptUrlData,
  LogOutSweetAlert,
} from "../../helpers/Reusable";
import { useContactStore } from "../../stores/contactStore";
import { useLegalStore } from "../../stores/legalStore";
import {
  clearCookies,
  getCookie,
  setCookie,
} from "../../helpers/CookieComponent";
import "./Sitemap.css";

const buildSections = ({
  youtubeHref,
  socialLinks,
  legalChildren,
  isAuth,
  onLogout,
}) => [
  {
    title: "Knowledge base",
    children: [
      { label: "Article", to: "/articlelist", menu: "Article" },
      { label: "Presentation", to: "/articlelist", menu: "Presentation" },
      { label: "Video", to: "/articlelist", menu: "Videos" },
      { label: "Queries", to: "/articlelist", menu: "Recent Question" },
      { label: "Youtube Video", href: youtubeHref },
      { label: "Latest Update", to: "/latestupdate" },
      { label: "Important Links", to: "/importantlinks", red: true },
      { label: "Download", to: "/download", red: true },
    ],
  },
  {
    title: "Product",
    children: [
      { label: "Advisory", to: "/advisory" },
      { label: "Ready Reference", to: "/readyreference" },
      { label: "Practice", to: "/practice" },
      { label: "Books", to: "/books" },
      { label: "Tax & Returns", to: "/taxreturns" },
      { label: "Training", to: "/training" },
      { label: "Webinar", to: "/webinar" },
    ],
  },
  isAuth
    ? {
        title: "Profile",
        children: [
          {
            label: "My Accounts",
            to: "/myaccount",
            children: [
              { label: "Personal Information", to: "/personalinfo" },
              { label: "Address", to: "/myaddress" },
              { label: "Tax Identification Number", to: "/mytaxidentification" },
              { label: "Change Password", to: "/changepassword" },
              { label: "Manage Interest", to: "/manageinterest" },
            ],
          },
          { label: "My Subscription", to: "/mysubscription" },
          { label: "My Order", to: "/myorder" },
          { label: "My Library", to: "/mylibrary" },
          { label: "Refer and Earn", to: "/referandearn" },
          { label: "Manage Newsletter", to: "/managenewsletter" },
          { label: "Log out", onClick: onLogout, red: true },
        ],
      }
    : {
        title: "Login",
        children: [
          { label: "Login", to: "/login" },
          { label: "Sign Up", to: "/register" },
        ],
      },
  {
    title: "Social Media",
    children: [
      { label: "Linkedin", href: socialLinks.linkedin },
      { label: "FaceBook", href: socialLinks.facebook },
      { label: "Whatsapp", href: socialLinks.whatsapp },
      { label: "Telegram", href: socialLinks.telegram },
      { label: "Youtube", href: socialLinks.youtube },
      { label: "Instagram", href: socialLinks.instagram },
      { label: "Gmail", href: socialLinks.gmail },
    ],
  },
  {
    title: "Legal",
    children: legalChildren,
  },
  {
    title: "Support",
    children: [
      {
        label: "Contact Us",
        to: `/home?gcc=${encodeURIComponent("Contact Us")}`,
      },
      {
        label: "Service Request",
        to: `/home?gcc=${encodeURIComponent("Service Request")}`,
      },
      {
        label: "Publish with Us",
        to: `/home?gcc=${encodeURIComponent("Publish With Us")}`,
      },
      {
        label: "Partner with Us",
        to: `/home?gcc=${encodeURIComponent("Partner With Us")}`,
      },
      {
        label: "Advertise with Us",
        to: `/home?gcc=${encodeURIComponent("Advertise With Us")}`,
      },
      {
        label: "Subscription",
        to: `/home?gcc=${encodeURIComponent("Subscribe")}`,
      },
      {
        label: "Provide Feedbback",
        to: `/home?gcc=${encodeURIComponent("Provide Feedback")}`,
      },
    ],
  },
  {
    title: "Company",
    children: [
      { label: "Vision & Mission", to: "/aboutus#visionmission" },
      { label: "Profile", to: "/aboutus#profile" },
      { label: "Location", to: "/location" },
    ],
  },
];

const NodeLink = ({ item, className }) => {
  if (item.href) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noreferrer"
        className={className}
      >
        {item.label}
      </a>
    );
  }
  if (item.onClick) {
    return (
      <button
        type="button"
        onClick={item.onClick}
        className={className}
      >
        {item.label}
      </button>
    );
  }
  const handleClick = () => {
    if (item.menu) {
      setCookie("menu", item.menu, {});
      setCookie("articleCategoryId", "", {});
    }
  };
  return (
    <Link
      to={item.to}
      target="_blank"
      rel="noreferrer"
      className={className}
      onClick={handleClick}
    >
      {item.label}
    </Link>
  );
};

const Sitemap = () => {
  ChangeTitle("Sitemap");

  const navigate = useNavigate();
  const contactList = useContactStore((state) => state.contactList);
  const legalList = useLegalStore((state) => state.legalList);
  const isAuth = !!(decrypt(getCookie("token")) || "");

  const handleLogout = async () => {
    const result = await LogOutSweetAlert();
    if (result.isConfirmed) {
      clearCookies();
      setCookie("log-modal", true, {});
      navigate("/", { replace: true });
      document.location.reload();
    }
  };
  const findDetail = (title) =>
    contactList?.find((item) => item?.title === title)?.detail || "#";

  const legalFallback = [
    { label: "Privacy Policy", to: "/privacypolicy" },
    { label: "End User Agreement", to: "/enduserlicenseagreement" },
    { label: "Disclaimer And Copyright", to: "/disclaimerandcopyrights" },
    { label: "Payment Terms", to: "/paymentterms" },
    { label: "Cancellation Policy", to: "/cancellationreturnrefundpolicy" },
  ];
  const legalChildren =
    legalList && legalList.length > 0
      ? legalList.map((item) => ({
          label: item.title,
          to: `/legal?t=${encryptUrlData({ id: item._id })}`,
        }))
      : legalFallback;

  const socialLinks = {
    linkedin: findDetail("LinkedIn"),
    facebook: findDetail("Facebook"),
    whatsapp: findDetail("Whatsapp"),
    telegram: findDetail("Telegram"),
    youtube: findDetail("You Tube"),
    instagram: findDetail("Instagram"),
    gmail: findDetail("Gmail"),
  };

  const sections = buildSections({
    youtubeHref: socialLinks.youtube,
    socialLinks,
    isAuth,
    onLogout: handleLogout,
    legalChildren,
  });

  return (
    <div className="sitemap-wrapper">
      <div className="sitemap-container">
        <div className="sitemap-home-wrap">
          <Link to="/home" target="_blank" rel="noreferrer" className="sitemap-home">
            Home
          </Link>
        </div>

        <div className="sitemap-connector" />

        <div className="sitemap-columns">
          {sections.map((section, i) => (
            <div className="sitemap-col" key={i}>
              <div className="sitemap-col-head">{section.title}</div>
              <div className="sitemap-col-body">
                {section.children.map((item, idx) => (
                  <React.Fragment key={idx}>
                    <NodeLink
                      item={item}
                      className={`sitemap-node`}
                    />
                    {item.children && item.children.length > 0 && (
                      <div className="sitemap-subgroup">
                        {item.children.map((sub, sIdx) => (
                          <NodeLink
                            key={sIdx}
                            item={sub}
                            className={`sitemap-node sitemap-subnode`}
                          />
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Sitemap;
